import React from 'react';
import ProfileCard from './ProfileCard';
import { shallowEqual, useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { useTranslation } from 'react-i18next';
import { BackButton } from '../../../../shared/components/button/BackButton';
import { UserRoles } from '../../../../enum/UserRoles';
import { Row } from 'react-bootstrap';

export interface IProfile {
    componentKey: string;
    image?: string;
    isFilled?: boolean;
    title: string;
    description?: string;
    role?: string[];
    currentUserType?: string;
    openFromSideMenu?: boolean;
    userType?: string;
    toggleHandler?: () => void;
}

function ProfileSettings() {
    const { t } = useTranslation();
    const {
        user: { userType, completionInfo, company, agencyCompany, hasSuppliers, hasClients, delivery },
    } = useSelector((state: IAllStates) => state.auth, shallowEqual);

    const isGlobalDiscovery = company?.package?.globalDiscovery;

    const profiles: IProfile[] = [
        {
            componentKey: 'iv-profile',
            image: '/icons/profile/user.png',
            isFilled: completionInfo?.profileInfo,
            title: `${t('brand.account.ivProfile')}`,
            description: `${t('brand.account.ivProfile_desc')}`,
            role: ['influencer'],
        },
        {
            componentKey: 'personal-info',
            image: '/icons/profile/id-card.png',
            isFilled: completionInfo?.personalInfo,
            title: `${t('brand.account.personal_info')}`,
            description: `${t('brand.account.personal_info_desc')}`,
            role: ['brand', 'influencer', 'brand_master', 'agency', 'agency_master'],
        },
        {
            componentKey: 'company-info',
            image: '/icons/profile/business-and-trade.png',
            isFilled: completionInfo?.companyInfo,
            title: `${t('brand.account.company_info')}`,
            description: `${t('brand.account.company_info_desc')}`,
            role: ['brand_master', 'agency_master'],
        },
        {
            componentKey: 'billing-information',
            image: '/icons/profile/online-shopping.png',
            isFilled: completionInfo?.billingInfo,
            title: `${t('brand.account.billing_information')}`,
            description: `${t('brand.account.billing_information_desc')}`,
            role: ['brand_master', 'influencer', 'agency_master'],
        },
        {
            componentKey: 'login-and-security',
            image: '/icons/profile/user.png',
            isFilled: undefined,
            title: `${t('brand.account.login_and_security')}`,
            description: `${t('brand.account.login_and_security_desc')}`,
            role: ['brand', 'influencer', 'brand_master', 'agency', 'agency_master'],
        },
        {
            componentKey: 'subscription-plan',
            image: '/icons/profile/presentation.png',
            isFilled: undefined,
            title: `${t('brand.account.subscription_plan')}`,
            description: `${t('brand.account.subscription_plan_desc')}`,
        },
        {
            componentKey: 'global-preferences',
            image: '/icons/profile/firewall.png',
            isFilled: undefined,
            title: `${t('brand.account.global_preferences')}`,
            description: `${t('brand.account.global_preferences_desc')}`,
            role: ['brand', 'influencer', 'brand_master', 'agency', 'agency_master'],
        },
        {
            componentKey: 'users-and-roles',
            image: '/icons/profile/user-role.png',
            isFilled: undefined,
            title: `${t('brand.account.users_and_roles')}`,
            description: `${t('brand.account.users_and_roles_desc')}`,
            role: ['brand_master', 'agency_master'],
        },
        {
            componentKey: 'price-list',
            image: '/icons/profile/img_price_list.png',
            isFilled: undefined,
            title: `${t('brand.account.price_list_modal.price_list')}`,
            description: `${t('brand.account.price_list_modal.price_list_desc')}`,
            role: ['influencer'],
        },
        {
            componentKey: 'invoices',
            image: '/icons/profile/invoice.svg',
            isFilled: undefined,
            title: `${t('influencer.profile.invoices')}`,
            description: `${t('influencer.profile.invoices_desc')}`,
            role: ['influencer', 'agency', 'agency_master'],
        },
        {
            componentKey: 'subscription-plan',
            image: '/icons/profile/presentation.png',
            isFilled: undefined,
            title: `${t('brand.account.subscription_plan')}`,
            description:
                company?.package?.name.toUpperCase() + ` (Fee ${Math.round((1 - Number(company?.fee ?? 0.0)) * 100)}%)`,
            role: ['brand', 'brand_master'],
        },
        {
            componentKey: 'contracts',
            image: '/icons/profile/presentation.png',
            isFilled: undefined,
            title: `${t('brand.account.contracts')}`,
            description: `${t('brand.account.contracts_desc')}`,
            role: ['brand', 'brand_master'],
        },
        // {
        //     componentKey: 'my-network',
        //     image: '/icons/profile/handshake.png',
        //     isFilled: undefined,
        //     title: `${t('brand.account.my_network')}`,
        //     description: `${t('brand.account.my_network_desc')}`,
        //     role: ['brand', 'brand_master'],
        // },
        {
            componentKey: 'clients',
            image: '/icons/profile/business-and-trade.png',
            isFilled: undefined,
            title: `${t('brand.account.clients')}`,
            description: `${t('brand.account.clients_desc')}`,
            role: ['brand_master', 'brand'],
        },
        {
            componentKey: 'suppliers',
            image: '/icons/profile/user.png',
            isFilled: undefined,
            title: `${t('brand.account.marketing_agency')}`,
            description: `${t('brand.account.marketing_agency_desc')}`,
            role: ['brand_master'],
        },
        {
            componentKey: 'content-creators',
            image: '/icons/profile/user-role.png',
            isFilled: undefined,
            title: `${t('brand.account.content_creators')}`,
            description: `${t('brand.account.content_creators_desc')}`,
            role: ['agency', 'agency_master'],
        },
        {
            componentKey: 'delivery-address',
            image: '/icons/profile/delivery-address.svg',
            isFilled: undefined,
            title: `${t('brand.account.delivery_data')}`,
            description: `${t('brand.account.delivery_data_desc')}`,
            role: ['influencer', 'agency', 'agency_master'],
        },
    ];

    const shouldRemoveCard = (componentKey: string) => {
        if (componentKey === 'billing-information') {
            return (userType === UserRoles.INFLUENCER && agencyCompany?.id) || isGlobalDiscovery;
        } else if (!company?.package?.isShownToUser && componentKey === 'subscription-plan') {
            return true;
        } else if (isGlobalDiscovery && componentKey === 'contracts') {
            return true;
        } else if (
            userType === UserRoles.INFLUENCER &&
            !!agencyCompany &&
            !!delivery &&
            !delivery?.useOwnAddress &&
            componentKey === 'delivery-address'
        ) {
            return true;
        }
    };
    const hiddenCardsMap: Record<string, boolean> = {
        clients: !hasClients,
        suppliers: !hasSuppliers,
    };
    return (
        <>
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-md-flex align-items-center">
                    <BackButton />
                    <h3 className="mb-0 mt-md-0 mt-2">{t('general.my_account')}</h3>
                </div>
            </div>
            <Row className="mt-5 mb-5 pb-5 pb-md-0">
                {profiles.map(
                    item =>
                        item.role?.includes(userType) &&
                        !shouldRemoveCard(item.componentKey) &&
                        !hiddenCardsMap[item.componentKey] && (
                            <div key={item.componentKey} className="col-lg-4 col-md-6 d-flex align-items-stretch mb-4">
                                <ProfileCard {...item} currentUserType={userType} openFromSideMenu={false} />
                            </div>
                        )
                )}
            </Row>
        </>
    );
}

export default ProfileSettings;
